/**
 * AssessmentLetterOutput: the letter as the family will read it, editable
 * in place (the drafter's click-to-edit grammar, via EditableProse).
 *
 * Header block (DATE / TO / FROM / RE), salutation, opening paragraphs,
 * recommendations (bold run-in heading + prose, resource bullets, numbered
 * steps), closing paragraphs, valediction, then the server-built
 * e-signature block, which is read-only: author identity is never edited
 * here (it comes from the team profile). Follow-up notes render beneath.
 *
 * The recommendations are the only paragraphs that move (LP-32): each
 * carries quiet Move up / Move down controls while editable, the first
 * with no up and the last with no down. A move renumbers every numbered
 * heading and every numbered reference in the prose
 * (src/lib/letterRecommendationOrder.ts) and bubbles the whole next
 * letter, so the new order is what Save stores and the download prints.
 *
 * When onResponseChange is absent (the drafts detail reuse) the letter is
 * read-only and carries the edit-in-Word nudge.
 */

import { SignatureBlockLines } from './SignatureBlockLines';
import { type ReactNode } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import type { AssessmentLetterBody, AssessmentLetterRecommendation } from '@/lib/api';
import { Button } from '@/components/primitives/Button';
import { moveRecommendation } from '@/lib/letterRecommendationOrder';
import { AssessmentFollowUpsBlock } from './AssessmentFollowUpsBlock';
import { EditableProse } from './AssessmentDraftOutput';
import { useSupportContact } from '@/hooks/useSupportContact';
import { contactSentence } from '@/lib/errorCopy';

export interface AssessmentLetterOutputProps {
  response: AssessmentLetterBody;
  /** Supplied on the tool page: every edit bubbles a full next-letter object. */
  onResponseChange?: (next: AssessmentLetterBody) => void;
}

const BODY_STYLE = { color: 'var(--color-ink)' } as const;
const SUBTLE_STYLE = { color: 'var(--color-sage-300)' } as const;

export function AssessmentLetterOutput({ response, onResponseChange }: AssessmentLetterOutputProps): ReactNode {
  const contact = useSupportContact();
  const editable = onResponseChange !== undefined;
  const patch = (next: Partial<AssessmentLetterBody>): void => {
    onResponseChange?.({ ...response, ...next });
  };
  const patchList = (key: 'opening' | 'closing', index: number, value: string): void => {
    patch({ [key]: response[key].map((p, i) => (i === index ? value : p)) });
  };
  const patchRecommendation = (index: number, next: Partial<AssessmentLetterRecommendation>): void => {
    patch({
      recommendations: response.recommendations.map((rec, i) => (i === index ? { ...rec, ...next } : rec)),
    });
  };
  const move = (from: number, to: number): void => {
    const next = moveRecommendation(response, from, to);
    if (next !== response) onResponseChange?.(next);
  };
  const leaf = (value: string, label: string, onChange?: (v: string) => void): ReactNode => (
    <EditableProse value={value} label={label} onChange={editable ? onChange : undefined} />
  );

  const { header, signature } = response;
  const toLines = header.to.split('\n').filter((l) => l.trim().length > 0);

  return (
    <div className="space-y-4" data-testid="assessment-letter-output">
      <section
        className="bg-paper border border-sage-800/[8%] rounded-[12px] p-4 space-y-3"
        aria-label="Summary Assessment Letter"
        data-testid="assessment-letter-body"
      >
        {editable && (
          <p className="text-xs" style={SUBTLE_STYLE} data-testid="assessment-letter-edit-hint">
            Click any paragraph to edit it directly.
          </p>
        )}

        {/* Header block: the server stamps it; the form is where it changes. */}
        <div
          className="space-y-0.5 text-sm pb-2 mb-2 border-b"
          style={{ borderColor: 'var(--color-sage-300)', ...BODY_STYLE }}
          data-testid="assessment-letter-header"
        >
          <p><span className="font-semibold">DATE:</span> {header.date}</p>
          <p className="flex gap-1">
            <span className="font-semibold">TO:</span>
            <span>
              {toLines.map((line, i) => (
                <span key={i} className="block" data-testid="assessment-letter-to-line">
                  {line}
                </span>
              ))}
            </span>
          </p>
          <p><span className="font-semibold">FROM:</span> {header.from}</p>
          <p><span className="font-semibold">RE:</span> {header.re}</p>
        </div>

        <div data-testid="assessment-letter-salutation">
          {leaf(response.salutation, 'salutation', (v) => patch({ salutation: v }))}
        </div>

        <div className="space-y-2" data-testid="assessment-letter-opening">
          {response.opening.map((p, i) => (
            <div key={i}>{leaf(p, `opening paragraph ${i + 1}`, (v) => patchList('opening', i, v))}</div>
          ))}
        </div>

        <div className="space-y-3">
          {response.recommendations.map((rec, i, all) => (
            <div key={i} className="space-y-1.5" data-testid="assessment-letter-recommendation">
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1 text-sm font-semibold" style={BODY_STYLE} data-testid="assessment-letter-recommendation-heading">
                  {leaf(rec.heading, `recommendation ${i + 1} heading`, (v) => patchRecommendation(i, { heading: v }))}
                </div>
                {/* The move controls: quiet, keyboard-reachable, and only
                    the moves that exist (no up on the first, no down on
                    the last). Nothing else in the letter moves. */}
                {editable && all.length > 1 && (
                  <span className="flex shrink-0 items-center gap-1" data-testid="assessment-letter-recommendation-moves">
                    {i > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="min-h-0 px-1 py-0.5"
                        aria-label={`Move recommendation ${i + 1} up`}
                        data-testid="assessment-letter-recommendation-up"
                        icon={<ChevronUp size={16} aria-hidden="true" />}
                        onClick={() => move(i, i - 1)}
                      />
                    )}
                    {i < all.length - 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="min-h-0 px-1 py-0.5"
                        aria-label={`Move recommendation ${i + 1} down`}
                        data-testid="assessment-letter-recommendation-down"
                        icon={<ChevronDown size={16} aria-hidden="true" />}
                        onClick={() => move(i, i + 1)}
                      />
                    )}
                  </span>
                )}
              </div>
              {rec.paragraphs.map((p, j) => (
                <div key={j}>
                  {leaf(p, `recommendation ${i + 1} paragraph ${j + 1}`, (v) =>
                    patchRecommendation(i, { paragraphs: rec.paragraphs.map((x, k) => (k === j ? v : x)) }),
                  )}
                </div>
              ))}
              {rec.resources.length > 0 && (
                <ul className="list-disc pl-6 space-y-0.5" data-testid="assessment-letter-resources">
                  {rec.resources.map((r, j) => (
                    <li key={j}>
                      {leaf(r, `recommendation ${i + 1} resource ${j + 1}`, (v) =>
                        patchRecommendation(i, { resources: rec.resources.map((x, k) => (k === j ? v : x)) }),
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {rec.steps.length > 0 && (
                <ol className="list-decimal pl-6 space-y-0.5" data-testid="assessment-letter-steps">
                  {rec.steps.map((s, j) => (
                    <li key={j}>
                      {leaf(s, `recommendation ${i + 1} step ${j + 1}`, (v) =>
                        patchRecommendation(i, { steps: rec.steps.map((x, k) => (k === j ? v : x)) }),
                      )}
                    </li>
                  ))}
                </ol>
              )}
            </div>
          ))}
        </div>

        <div className="space-y-2" data-testid="assessment-letter-closing">
          {response.closing.map((p, i) => (
            <div key={i}>{leaf(p, `closing paragraph ${i + 1}`, (v) => patchList('closing', i, v))}</div>
          ))}
        </div>

        <div data-testid="assessment-letter-valediction">
          {leaf(response.valediction, 'valediction', (v) => patch({ valediction: v }))}
        </div>

        {/* The e-signature block: built from the team profile, never edited here. */}
        {signature.name_line.length > 0 && (
          <div className="text-sm space-y-0.5" style={BODY_STYLE} data-testid="assessment-letter-signature">
            <p>
              <span className="text-lg italic" data-testid="assessment-letter-signature-name">
                {signature.name_line}
              </span>{' '}
              <span className="text-xs italic text-ink-soft" data-testid="assessment-letter-signature-esign">
                {signature.esign_line}
              </span>
            </p>
            <p>{signature.credentials_line}</p>
            <SignatureBlockLines block={signature.designation_line} />
          </div>
        )}

        {response.signatory_missing === true && (
          <div
            role="status"
            className="mt-2 rounded-[10px] border border-warm-200 bg-warm-50 px-3 py-2 text-sm text-warm-700"
            data-testid="assessment-letter-signatory-missing"
          >
            {contactSentence(
              "This letter has no signature because your name isn't on your team profile yet. Ask {contact_admin} to add your name on the Team page, then generate again.",
              contact,
            )}
          </div>
        )}
      </section>

      <AssessmentFollowUpsBlock questions={response.follow_up_questions} />

      {editable ? (
        <p className="text-xs" style={SUBTLE_STYLE} data-testid="edit-inline-nudge">
          Click a paragraph to fix wording directly, then save. The downloaded Word file is the
          letter the family receives.
        </p>
      ) : (
        <p className="text-xs" style={SUBTLE_STYLE} data-testid="edit-in-word-nudge">
          To make edits, download and finalize in Word.
        </p>
      )}
    </div>
  );
}
